import React from "react";
import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter } from "react-icons/fa";

const defaultSections = [
  {
    title: "Product",
    links: [
      { name: "Overview", href: "#" },
      { name: "Pricing", href: "#" },
      { name: "Marketplace", href: "#" },
      { name: "Features", href: "#" },
    ],
  },
  {
    title: "Company",
    links: [
      { name: "About", href: "#" },
      { name: "Team", href: "#" },
      { name: "Blog", href: "#" },
      { name: "Careers", href: "#" },
    ],
  },
  {
    title: "Resources",
    links: [
      { name: "Help", href: "#" },
      { name: "Sales", href: "#" },
      { name: "Advertise", href: "#" },
      { name: "Privacy", href: "#" },
    ],
  },
];

const defaultSocialLinks = [
  { icon: <FaInstagram className="size-5" />, href: "#", label: "Instagram" },
  { icon: <FaFacebook className="size-5" />, href: "#", label: "Facebook" },
  { icon: <FaTwitter className="size-5" />, href: "#", label: "Twitter" },
  { icon: <FaLinkedin className="size-5" />, href: "#", label: "LinkedIn" },
];

const defaultLegalLinks = [
  { name: "Terms and Conditions", href: "#" },
  { name: "Privacy Policy", href: "#" },
];

export const Footer7 = ({
  logo = {
    url: "https://www.shadcnblocks.com",
    src: "https://www.shadcnblocks.com/images/block/logos/shadcnblockscom-icon.svg",
    alt: "logo",
    title: "Shadcnblocks.com",
  },

  sections = defaultSections,
  description = "A collection of components for your startup business or side project.",
  socialLinks = defaultSocialLinks,
  copyright = "© 2024 Shadcnblocks.com. All rights reserved.",
  legalLinks = defaultLegalLinks
}) => {
  const contactSection = sections.find(
    (section) => section.title && section.title.toLowerCase() === "contacto"
  );
  const mainSections = sections.filter((section) => section !== contactSection);
  const servicesSection = mainSections.find(
    (section) => section.title && section.title.toLowerCase() === "servicios"
  );
  const otherSections = mainSections.filter((section) => section !== servicesSection);
  const isExternalLink = (href) =>
    typeof href === "string" &&
    (/^https?:\/\//i.test(href) || href.startsWith("mailto:"));
  const isEmailLink = (link) =>
    typeof link?.name === "string" && link.name.includes("@");
  const getLinkItemClass = (link) =>
    `font-medium${link?.href ? " hover:text-primary" : ""}${
      isEmailLink(link) ? " whitespace-nowrap text-xs sm:text-sm" : ""
    }`;

  const renderLink = (link) => {
    if (!link.href) {
      return <span>{link.name}</span>;
    }

    return (
      <a
        href={link.href}
        {...(isExternalLink(link.href)
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {link.name}
      </a>
    );
  };

  const getSectionListClass = (sectionTitle) =>
    `space-y-3 text-sm text-muted-foreground${
      sectionTitle && sectionTitle.toLowerCase() === "contacto"
        ? " leading-relaxed break-words"
        : ""
    }`;

  return (
    <section className="py-16 sm:py-20 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="block lg:hidden">
          <div className="grid grid-cols-2 gap-6 text-left">
            <div className="flex w-full flex-col justify-between gap-5">
              <div className="flex items-center gap-2">
                <a href={logo.url}>
                  <img src={logo.src} alt={logo.alt} title={logo.title} className="h-8" />
                </a>
                <h2 className="text-base font-semibold">{logo.title}</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                {description}
              </p>
              <ul className="flex flex-wrap items-center gap-x-4 gap-y-2 text-muted-foreground">
                {socialLinks.map((social, idx) => (
                  <li key={idx} className="font-medium hover:text-primary">
                    <a
                      href={social.href}
                      aria-label={social.label}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {social.icon}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {servicesSection && (
              <div>
                <h3 className="mb-4 font-bold">{servicesSection.title}</h3>
                <ul className={getSectionListClass(servicesSection.title)}>
                  {servicesSection.links.map((link, linkIdx) => (
                    <li key={linkIdx} className={getLinkItemClass(link)}>
                      {renderLink(link)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-8 text-left">
            {contactSection && (
              <div className="col-span-2">
                <h3 className="mb-4 font-bold">{contactSection.title}</h3>
                <ul className={getSectionListClass(contactSection.title)}>
                  {contactSection.links.map((link, linkIdx) => (
                    <li key={linkIdx} className={getLinkItemClass(link)}>
                      {renderLink(link)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {otherSections.map((section, sectionIdx) => (
              <div key={sectionIdx}>
                <h3 className="mb-4 font-bold">{section.title}</h3>
                <ul className={getSectionListClass(section.title)}>
                  {section.links.map((link, linkIdx) => (
                    <li key={linkIdx} className={getLinkItemClass(link)}>
                      {renderLink(link)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="hidden lg:flex w-full flex-col justify-between gap-10 lg:flex-row lg:items-start lg:text-left">
          <div className="flex w-full flex-col justify-between gap-6 lg:items-start">
            {/* Logo */}
            <div className="flex items-center gap-2 lg:justify-start">
              <a href={logo.url}>
                <img src={logo.src} alt={logo.alt} title={logo.title} className="h-8" />
              </a>
              <h2 className="text-xl font-semibold">{logo.title}</h2>
            </div>
            <p className="max-w-[70%] text-sm text-muted-foreground">
              {description}
            </p>
            <ul className="flex items-center space-x-6 text-muted-foreground">
              {socialLinks.map((social, idx) => (
                <li key={idx} className="font-medium hover:text-primary">
                  <a
                    href={social.href}
                    aria-label={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {social.icon}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid w-full gap-8 text-left lg:grid-cols-3 lg:gap-20">
            {sections.map((section, sectionIdx) => (
              <div key={sectionIdx}>
                <h3 className="mb-4 font-bold">{section.title}</h3>
                <ul className={getSectionListClass(section.title)}>
                  {section.links.map((link, linkIdx) => (
                    <li key={linkIdx} className={getLinkItemClass(link)}>
                      {renderLink(link)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div
          className="mt-8 flex flex-col justify-between gap-4 border-t py-6 text-xs font-medium text-muted-foreground md:flex-row md:items-center md:text-left">
          <p className="order-2 text-center md:order-1 md:text-left">{copyright}</p>
          <ul className="order-1 flex flex-col items-center gap-2 md:order-2 md:flex-row md:items-center">
            {legalLinks.map((link, idx) => (
              <li key={idx} className="hover:text-primary">
                {renderLink(link)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

